/**
 * GLTFMeshComponent loads and displays a glTF model within the scene.
 * Handles asynchronous loading through GLTFLoader with optional material overrides.
 * Exposes glTF data for animations and reports loading status through callbacks.
 */
import * as THREE from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { BundleGroup } from 'three/webgpu';

import { AssetPath } from '../../storage/storageProvider.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { ClassRegistry } from '../../systems/ClassRegistry.js';
import { getLatest } from '../../utils/Async.js';
import { Delegate } from '../../utils/Delegate.js';
import { AsyncTaskWorker, IAsyncLoadableTag } from '../../utils/IAsyncLoadable.js';
import { resourceManager } from '../../utils/ResourceManager.js';
import { ModelPath, property } from '../../utils/serialization/decorator.js';
import { isNode } from '../../utils/Utils.js';
import { PrimitiveComponent } from '../PrimitiveComponent.js';
import { type ComponentDescriptionOptions, SceneComponent } from '../SceneComponent.js';

import { MeshSocket } from './MeshSocket.js';

import type { GenericMaterialType } from '../../materials.js';
import type { EditorPropertyChangedResult } from '../../utils/EditorClassMeta.js';
import type { IAsyncLoadable } from '../../utils/IAsyncLoadable.js';
import type { PrimitiveComponentOptions } from '../PrimitiveComponent.js';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';

export interface ModelMeshComponentOptions extends PrimitiveComponentOptions {
  modelUrl?: string;
  material?: GenericMaterialType;
  materials?: Array<GenericMaterialType>;
  useDynamicMaterials?: boolean;
  sockets?: MeshSocket[];
  /** Whether to bundle the model into a single render bundle (WebGPU only). */
  useBundleGroup?: boolean;
}

export type ModelMeshLoadedDelegate = Delegate<[ModelMeshComponent, THREE.Object3D], void>;
export type ModelMeshSocketsChangedDelegate = Delegate<[ModelMeshComponent], void>;
@EngineClass('ModelMeshComponent')
export class ModelMeshComponent extends PrimitiveComponent implements IAsyncLoadable {
  readonly [IAsyncLoadableTag] = true;
  /** The number of meshes above which the model will be bundled into a single render bundle. */
  static MeshBundlingThreshold = 5;
  public readonly onMeshLoaded: ModelMeshLoadedDelegate = new Delegate<[ModelMeshComponent, THREE.Object3D], void>();
  public readonly onSocketsChanged: ModelMeshSocketsChangedDelegate = new Delegate<[ModelMeshComponent], void>();

  private _useBundleGroup: boolean = false;

  private model: THREE.Object3D | null = null;
  /** The last loaded glTF result, including animations and other data */
  private gltf: GLTF | null = null;

  @property({ required: true, description: 'Model URL' })
  public modelUrl: ModelPath | null = null;

  @property()
  public modelMaterial: GenericMaterialType | null = null;

  @property()
  public modelMaterials: Array<GenericMaterialType> | null = null;

  @property()
  protected useDynamicMaterials: boolean = false;

  @property({ description: 'Whether to bundle the model into a single render bundle (WebGPU only)' })
  get useBundleGroup(): boolean {
    return this._useBundleGroup;
  }
  set useBundleGroup(value: boolean) {
    if (value === this._useBundleGroup) return;
    this._useBundleGroup = value;
    if (this.model && this.modelUrl) {
      // reload the model to take effect
      this.loadModel(AssetPath.fromString(this.modelUrl));
    }
  }

  @property({
    type: 'array'
  })
  public sockets: MeshSocket[] = [];

  private spawnedSocketsMap: Map<string, THREE.Object3D> = new Map();

  private _asyncTaskWorker = new AsyncTaskWorker();


  public override initialize(options?: ModelMeshComponentOptions): void {
    super.initialize(options);
    this.useDynamicMaterials = options?.useDynamicMaterials ?? this.useDynamicMaterials;
    this.useBundleGroup = options?.useBundleGroup ?? this.useBundleGroup;
    this.modelMaterial = options?.material ?? this.modelMaterial;
    this.modelMaterials = options?.materials ?? this.modelMaterials;
    this.modelUrl = options?.modelUrl ?? this.modelUrl;
    this.sockets = options?.sockets ?? this.sockets;

    // don't call loadModel here - wait until beginPlay
    // as this can be a very common pattern, and its gonna be tricky to "stop" an async load:
    //
    // const component = MyGLTFMeshComponent.create();  // where default modelUrl is set in constructor
    // component.modelUrl = 'some-other-model.glb';
  }

  public override postLoad() {
    super.postLoad();

    // load the model if modelUrl is set in saved data - scene
    // this is useful so that scene can be fully loaded with models before preStart of gameLoop is called
    if (this.modelUrl) {
      this.loadModel(AssetPath.fromString(this.modelUrl));
    }
  }

  public async waitForLoad(): Promise<void> {
    await this._asyncTaskWorker.waitUntilComplete();
  }

  public isLoading(): boolean {
    return this._asyncTaskWorker.isRunning();
  }

  public override beginPlay(): void {
    super.beginPlay();
    if (this.modelUrl && !this.gltf && !this.isLoading()) {  // not already loaded, or being loaded by postLoad
      this.loadModel(AssetPath.fromString(this.modelUrl));
    }
  }

  public async loadModel(path: AssetPath): Promise<void> {
    this.modelUrl = path.initialPath;
    await this._internalPerformModelLoad(path);
  }

  private async _internalPerformModelLoad(path: AssetPath): Promise<void> {
    await this._asyncTaskWorker.runTask(async () => {
      this.modelUrl = path.initialPath;

      // Use getLatest to prevent race conditions when model URL changes rapidly
      const gltf = await getLatest(this, 'model', () => resourceManager.loadModel(path));
      if (gltf === undefined) {
        return; // a newer load superseded this one
      }

      // always remove the current gltf, if loading fails. User can be trying to load '' to clear the mesh on purpose
      // If load fails, it makes sense to remove the gltf mesh as well. So we always do it here
      this.model?.removeFromParent();
      this.gltf = gltf ?? null;

      if (!gltf) {
        return; // load failed
      }

      // NOTE: the returned gltf is the same instance under the same url so we need to instantiate for this particular component
      const clonedModel = SkeletonUtils.clone(gltf.scene);
      const meshes: THREE.Mesh[] = [];
      clonedModel.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          meshes.push(child);
          this.bindObject3DProperties(child, SceneComponent.boundObject3DProperties);
        }
      });
      let model: THREE.Object3D;
      if (this.useBundleGroup && meshes.length >= ModelMeshComponent.MeshBundlingThreshold) {
        // Flatten meshes into a BundleGroup so WebGPU can record them as a single render bundle.
        const bundleGroup = new BundleGroup();
        clonedModel.updateMatrixWorld(true);
        // BundleGroup replaces the glTF scene root, so carry over its transform.
        bundleGroup.position.copy(clonedModel.position);
        bundleGroup.quaternion.copy(clonedModel.quaternion);
        bundleGroup.scale.copy(clonedModel.scale);

        // Reparenting drops intermediate node transforms; bake each mesh's world pose into
        // bundle-local space before moving it under the flat BundleGroup hierarchy.
        const rootInverse = new THREE.Matrix4().copy(clonedModel.matrixWorld).invert();
        const localMatrix = new THREE.Matrix4();
        for (const mesh of meshes) {
          localMatrix.multiplyMatrices(rootInverse, mesh.matrixWorld);
          bundleGroup.add(mesh);
          localMatrix.decompose(mesh.position, mesh.quaternion, mesh.scale);
          mesh.updateMatrix();
        }
        model = bundleGroup;
      } else {
        model = clonedModel;
      }
      this.model = model;

      if (!isNode()) {
        if (this.modelMaterial) {
          await this.setMaterial(this.modelMaterial);
        } else if (this.modelMaterials) {
          await this.setMaterials(this.modelMaterials);
        }
      }

      this.add(this.model);
      this.updateMatrixWorld(true);
      this.gltf = gltf;

      // Refresh physics representation if component is already in scene and playing
      this.refreshPhysics();

      this.rebuildSockets();

      // Clone materials if useDynamicMaterials is enabled and we're playing
      // Must happen AFTER materials are loaded and applied
      if (this.useDynamicMaterials && this.getActor()?.isPlaying()) {
        this.cloneMaterials();
      }

      // Call all callbacks
      this.onMeshLoaded.invoke(this, model);
    });
  }

  private cloneMaterials(): void {
    if (!this.model) return;

    this.model.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        if (Array.isArray(child.material)) {
          // Clone each material in the array
          child.material = child.material.map(mat => mat.clone());
        } else {
          // Clone single material
          child.material = child.material.clone();
        }
      }
    });
  }

  public async setMaterial(material: GenericMaterialType): Promise<void> {
    // TODO: potential race condition even with `getLatest`:
    // setMaterial('mat1'); setMaterial('');  -- second call may finish first and be overwritten by first call finishing later

    if (typeof material === 'string') {
      this.modelMaterial = material;
      if (!material) {
        // TODO: better way to reset to default material?
        if (!material && typeof this.modelUrl === 'string') {
          await this.loadModel(AssetPath.fromString(this.modelUrl));
          return;
        }
      }
    }

    // Use getLatest to prevent race conditions when material changes rapidly
    const loadedMaterial = await getLatest(
      this,
      'modelMaterial',
      () => resourceManager.loadGenericMaterial(material)
    );
    if (loadedMaterial) {
      const allMeshes = this.getAllMeshes();
      for (const mesh of allMeshes) {
        mesh.material = loadedMaterial;
      }
    }
  }

  public async setMaterials(materials: Array<GenericMaterialType>): Promise<void> {
    this.modelMaterials = materials;

    // Use getLatest to prevent race conditions when materials change rapidly
    const loadedMaterials = await getLatest(
      this,
      'modelMaterials',
      () => Promise.all(materials.map(mat => resourceManager.loadGenericMaterial(mat)))
    );

    // Only apply if this is still the latest call
    if (loadedMaterials) {
      const allMeshes = this.getAllMeshes();
      loadedMaterials.forEach((material, index) => {
        if (material && index < allMeshes.length) {
          allMeshes[index].material = material;
        }
      });
    }
  }

  public getMaterialBySlot(index: number): THREE.Material | THREE.Material[] | null {
    const allMeshes = this.getAllMeshes();
    if (index < 0 || index >= allMeshes.length) {
      return null;
    }
    return allMeshes[index].material;
  }

  public async setMaterialBySlot(index: number, material: GenericMaterialType): Promise<void> {
    const allMeshes = this.getAllMeshes();
    if (index < 0 || index >= allMeshes.length) {
      console.warn(`GLTFMeshComponent: Invalid slot index ${index}. Valid range is 0-${allMeshes.length - 1}`);
      return;
    }

    // Use getLatest with slot-specific key to prevent race conditions
    const loadedMaterial = await getLatest(
      this,
      `materialSlot_${index}`,
      () => resourceManager.loadGenericMaterial(material)
    );
    if (loadedMaterial) {
      allMeshes[index].material = loadedMaterial;
    }
  }

  public getModel(): THREE.Object3D | null {
    return this.model;
  }

  public getModelTemplate(): THREE.Object3D | null {
    return this.gltf?.scene ?? null;
  }

  public getAnimations(): THREE.AnimationClip[] {
    return this.gltf?.animations ?? [];
  }

  public getSkinnedMeshData(): { skinnedMesh: THREE.SkinnedMesh; skeleton: THREE.Skeleton } | null {
    if (!this.model) return null;
    return this.getAllSkinnedMeshes()[0] ?? null;
  }

  public getAllSkinnedMeshes(): Array<{ skinnedMesh: THREE.SkinnedMesh; skeleton: THREE.Skeleton }> {
    if (!this.model) return [];

    const results: Array<{ skinnedMesh: THREE.SkinnedMesh; skeleton: THREE.Skeleton }> = [];

    for (const mesh of this.getAllMeshes()) {
      if (mesh instanceof THREE.SkinnedMesh) {
        if (mesh.skeleton && mesh.skeleton.bones && mesh.skeleton.bones.length > 0) {
          results.push({
            skinnedMesh: mesh,
            skeleton: mesh.skeleton
          });
        }
      }
    }

    return results;
  }

  public getMesh(): THREE.Mesh | null {
    return this.getAllMeshes()[0] ?? null;
  }

  public getAllMeshes(): THREE.Mesh[] {
    const meshes: THREE.Mesh[] = [];
    this.model?.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        meshes.push(object);
      }
    });
    return meshes;
  }

  public isModelLoaded(): boolean {
    return !!this.model;
  }

  public rebuildSockets(): void {
    // Clear existing sockets
    this.spawnedSocketsMap.forEach(socket => socket.removeFromParent());
    this.spawnedSocketsMap.clear();

    if (!this.model) return;

    // Build a map of all bones by name
    const bonesMap = new Map<string, THREE.Bone>();
    this.model.traverse((child) => {
      if ((child as THREE.Bone).isBone) {
        bonesMap.set(child.name, child as THREE.Bone);
      }
    });

    for (const socketDef of this.sockets) {
      if (!socketDef.name || !socketDef.boneName) continue;

      const bone = bonesMap.get(socketDef.boneName);
      if (bone) {
        const socketObj = new THREE.Object3D();
        socketObj.name = `Socket_${socketDef.name}`;
        socketObj.position.copy(socketDef.offsetLocation);
        socketObj.rotation.copy(socketDef.offsetRotation);
        socketObj.scale.copy(socketDef.offsetScale);

        // Override matrix updating to remove parent bone scaling
        socketObj.updateMatrixWorld = function (force?: boolean) {
          if (this.matrixAutoUpdate) this.updateMatrix();

          if (this.matrixWorldNeedsUpdate || force) {
            if (this.parent) {
              // Get parent world matrix, but remove its scale component
              const parentMatrix = this.parent.matrixWorld.clone();
              const position = new THREE.Vector3();
              const quaternion = new THREE.Quaternion();
              const scale = new THREE.Vector3();

              parentMatrix.decompose(position, quaternion, scale);

              // Recompose the parent matrix without its scale (using 1,1,1)
              parentMatrix.compose(position, quaternion, new THREE.Vector3(1, 1, 1));

              // Apply our local matrix to the unscaled parent matrix
              this.matrixWorld.multiplyMatrices(parentMatrix, this.matrix);
            } else {
              this.matrixWorld.copy(this.matrix);
            }

            this.matrixWorldNeedsUpdate = false;
            force = true;
          }

          // Update children
          const children = this.children;
          for (let i = 0, l = children.length; i < l; i++) {
            const child = children[i];
            if (child.matrixWorldAutoUpdate === true || force === true) {
              child.updateMatrixWorld(force);
            }
          }
        };

        bone.add(socketObj);
        this.spawnedSocketsMap.set(socketDef.name, socketObj);
      } else {
        console.warn(`GLTFMeshComponent: Could not find bone '${socketDef.boneName}' for socket '${socketDef.name}'`);
      }
    }

    // Notify that sockets have been rebuilt
    this.onSocketsChanged.invoke(this);
  }

  public getSocket(socketName: string): THREE.Object3D | null {
    return this.spawnedSocketsMap.get(socketName) ?? null;
  }

  public getSocketNames(): string[] {
    return Array.from(this.spawnedSocketsMap.keys());
  }

  public getAvailableBones(): string[] {
    if (!this.model) return [];

    const bones: string[] = [];
    this.model.traverse((child) => {
      if ((child as THREE.Bone).isBone) {
        bones.push(child.name);
      }
    });
    return bones;
  }

  public override onEditorAddToWorld(): void {
    super.onEditorAddToWorld();
    if (this.modelUrl) {
      this._internalPerformModelLoad(AssetPath.fromString(this.modelUrl));
    }
  }

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);
    if (path === 'modelUrl') {
      this._internalPerformModelLoad(AssetPath.fromString(value));
    } else if (path === 'modelMaterial') {
      this.setMaterial(value);
    } else if (path.startsWith('modelMaterials')) {
      // If material (singular) is set, it takes precedence
      if (!this.modelMaterial && this.modelMaterials) {
        this.setMaterials(this.modelMaterials);
      }
    } else if (path.startsWith('sockets')) {
      this.rebuildSockets();
    }
  }

  public override calcBoundingBox(): void {
    this._boundingBox = ModelMeshComponent.calcBoundingBoxFromGLTFModel(this.getModel(), this.getActor()?.name);
  }

  public static calcBoundingBoxFromGLTF(gltf: GLTF | null): THREE.Box3 {
    if (!gltf || !gltf.scene) {
      console.warn('GLTFMeshComponent: No GLTF scene available to calculate bounding box. Has the model finished loading?');
      return new THREE.Box3();
    }
    return ModelMeshComponent.calcBoundingBoxFromGLTFModel(gltf.scene);
  }

  public static calcBoundingBoxFromGLTFModel(gltfScene: THREE.Object3D | null, context?: any): THREE.Box3 {
    if (!gltfScene) {
      console.warn('GLTFMeshComponent: No GLTF scene available to calculate bounding box. Has the model finished loading?', context);
      return new THREE.Box3();
    }

    gltfScene.updateMatrixWorld(true);

    const boundingBox = new THREE.Box3();
    const box = new THREE.Box3();
    gltfScene.traverse((child: THREE.Object3D) => {
      if (!(child instanceof THREE.Mesh)) {
        return;
      }

      if (child instanceof THREE.SkinnedMesh) {
        // Recompute so bounds match the current skeleton pose.
        child.computeBoundingBox();
        if (!child.boundingBox) {
          return;
        }
        box.copy(child.boundingBox);
      } else {
        child.geometry.computeBoundingBox();
        if (!child.geometry.boundingBox) {
          return;
        }
        box.copy(child.geometry.boundingBox);
      }

      box.applyMatrix4(child.matrixWorld);
      boundingBox.union(box);
    });

    return boundingBox;
  }

  public override describe(options?: ComponentDescriptionOptions): Record<string, any> {
    const result = super.describe(options);
    if (options?.includeDetails) {
      result.modelUrl = this.modelUrl;
      result.material = this.modelMaterial;
      result.materials = this.modelMaterials;
      result.useDynamicMaterials = this.useDynamicMaterials;
      result.useBundleGroup = this.useBundleGroup;
    };
    return result;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_GLTFMesh';
  }
}

ClassRegistry.addClassesToRedirect({
  'ENGINE.GLTFMeshComponent': 'ENGINE.ModelMeshComponent',
});

/**
 * @deprecated Use {@link ModelMeshComponent} instead.
 */
export const GLTFMeshComponent = ModelMeshComponent;

/**
 * @deprecated Use {@link ModelMeshComponent} instead.
 */
export type GLTFMeshComponent = ModelMeshComponent;

/**
 * @deprecated Use {@link ModelMeshComponentOptions} instead.
 */
export type GLTFMeshComponentOptions = ModelMeshComponentOptions;

/**
 * @deprecated Use {@link ModelMeshLoadedDelegate} instead.
 */
export type GLTFMeshLoadedDelegate = ModelMeshLoadedDelegate;

/**
 * @deprecated Use {@link ModelMeshSocketsChangedDelegate} instead.
 */
export type GLTFSocketsChangedDelegate = ModelMeshSocketsChangedDelegate;
